import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Package, ArrowRight } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";
import CommentThread from "../components/CommentThread.jsx";

const STATUS_STYLE = {
  "OPEN": "bg-blue-50 text-blue-800 border-blue-300",
  "REJECTED": "bg-red-50 text-red-800 border-red-300",
  "ORDERED": "bg-indigo-50 text-indigo-800 border-indigo-300",
  "ALOCADO": "bg-purple-50 text-purple-800 border-purple-300",
  "IN TRANSIT": "bg-yellow-50 text-yellow-800 border-yellow-300",
  "RECEIVED": "bg-teal-50 text-teal-800 border-teal-300",
  "INCOMPLETE": "bg-orange-50 text-orange-800 border-orange-300",
  "READY FOR COLLECTION": "bg-emerald-50 text-emerald-800 border-emerald-300",
  "CLIENT CONTACTED": "bg-green-50 text-green-800 border-green-300",
  "CLOSED": "bg-gray-100 text-gray-700 border-gray-300",
  "REOPENED": "bg-cyan-50 text-cyan-800 border-cyan-300",
};

export default function PartsBenchOrderDetailPage() {
  const { id } = useParams();
  const [peb, setPeb] = useState(null);
  const [enums, setEnums] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const [{ data: p }, { data: e }] = await Promise.all([
      api.get(`/parts-bench-orders/${id}`),
      api.get(`/parts-bench-orders/meta/enums`),
    ]);
    setPeb(p); setEnums(e);
  };
  useEffect(() => { load(); }, [id]);
  if (!peb || !enums) return <div className="p-8 text-sm text-[#6C757D]">A carregar…</div>;

  const allowed = enums.allowed_transitions[peb.current_status] || [];

  const doTransition = async (newStatus) => {
    setErr("");
    let comment = null;
    if (["REJECTED", "CLOSED"].includes(newStatus)) {
      comment = prompt(`Motivo para ${newStatus}:`);
      if (!comment) return;
    }
    try {
      const { data } = await api.post(`/parts-bench-orders/${id}/status`, { new_status: newStatus, comment });
      setPeb(data);
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  const setTransitStage = async (stage) => {
    setErr("");
    try {
      const { data } = await api.post(`/parts-bench-orders/${id}/transit-stage`, { transit_stage: stage });
      setPeb(data);
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm" data-testid="peb-header">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/parts-bench-orders" className="flex items-center gap-1 hover:text-[#1F3864]"><ChevronLeft className="w-3 h-3" /> Encomendas de Balcão</Link>
          <span>/</span>
          <span className="font-mono">{peb.number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <Package className="w-5 h-5 text-[#1F3864]" />
              <h1 className="font-heading text-xl font-bold" data-testid="peb-title">{peb.number} · {peb.customer_name}</h1>
              <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_STYLE[peb.current_status]}`} data-testid="peb-status-badge">{peb.current_status}</span>
              {peb.current_status === "IN TRANSIT" && peb.transit_stage && (
                <span className="text-[10px] uppercase tracking-wider border border-gray-300 bg-gray-50 text-gray-700 rounded-sm px-1.5 py-0.5" data-testid="peb-transit-badge">
                  {enums.transit_stages.find(t => t.key === peb.transit_stage)?.label}
                </span>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span>Ref ERP: <span className="font-mono text-[#1F3864]">{peb.erp_reference}</span></span>
              <span>{peb.customer_phone}</span>
              {peb.customer_email && <span>{peb.customer_email}</span>}
              <span>Dealer: {peb.branch}</span>
              {peb.is_import && <span className="text-[#7A4E00]">📦 Importação</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="peb-error">{err}</div>}

        {/* Actions */}
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">Acções disponíveis</h3>
          <div className="flex flex-wrap gap-2">
            {allowed.length === 0 && <span className="text-xs text-[#6C757D]">Sem transições possíveis a partir deste estado.</span>}
            {allowed.map(s => (
              <button key={s} onClick={() => doTransition(s)} data-testid={`peb-btn-${s.replace(/\s+/g, "-")}`} className="text-xs border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-1.5 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" /> {s}
              </button>
            ))}
          </div>
          {peb.current_status === "IN TRANSIT" && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">Sub-fase de trânsito</div>
              <div className="flex gap-2">
                {enums.transit_stages.map(t => (
                  <button key={t.key} onClick={() => setTransitStage(t.key)} data-testid={`peb-transit-${t.key}`}
                    className={`text-xs rounded-sm px-3 py-1 border ${peb.transit_stage === t.key ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 hover:border-[#1F3864]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="bg-white border border-gray-200 rounded-md p-5 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Ref ERP"><span className="font-mono text-[#1F3864]">{peb.erp_reference}</span></Row>
          <Row label="Cliente">{peb.customer_name}{peb.customer && <Link to={`/customers/${peb.customer.id}`} className="ml-2 text-[10px] text-[#1F3864] font-mono hover:underline">({peb.customer.customer_number})</Link>}</Row>
          <Row label="Telefone">{peb.customer_phone}</Row>
          <Row label="Email">{peb.customer_email || "—"}</Row>
          <Row label="Dealer">{peb.branch}</Row>
          <Row label="Prioridade">{peb.priority}</Row>
          <Row label="Importação">{peb.is_import ? "Sim" : "Não"}</Row>
          <Row label="Criado">{fmtDate(peb.created_at)} · {peb.created_by_name || "—"}</Row>
          <div className="col-span-2"><Row label="Notas">{peb.notes || "—"}</Row></div>
        </div>

        {/* Lines */}
        {peb.lines && peb.lines.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-md">
            <div className="px-4 py-2 border-b bg-[#F4F6F8] text-xs font-bold uppercase tracking-wider">Linhas de peças ({peb.lines.length})</div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#6C757D]"><tr><Th>Nº peça</Th><Th>Descrição</Th><Th>Qtd</Th><Th>Fonte</Th><Th>Notas</Th></tr></thead>
              <tbody>
                {peb.lines.map((l, i) => (
                  <tr key={i} className="border-t border-gray-100"><Td mono>{l.part_number}</Td><Td>{l.description}</Td><Td>{l.quantity_ordered}</Td><Td className="text-[#6C757D]">{l.source || "—"}</Td><Td className="text-[#6C757D]">{l.notes || "—"}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CommentThread recordType="parts_bench_order" recordId={id} />
      </div>
    </div>
  );
}

function Row({ label, children }) { return (<div><div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div><div className="text-sm text-[#1A1A2E]">{children}</div></div>); }
function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }
